/**
 * The public Market Intel API (ADR-033 §4.6), as Gluon calls it.
 *
 * MARKET DATA, NOT AUDIT DATA. What a lane pays and where freight is hot come
 * from the market-intel module's snapshot, over the same routes every other
 * client uses — Gluon keeps no second way of computing them. The operator
 * audit (what the pipeline wrote) stays in `./loadPipeline`, behind
 * main-service.
 *
 * These routes mount at the loads-ingest ROOT (`/market-intel/...`), not under
 * `/v1`, hence `loadsIngestServiceRootAxios`. The user's JWT is the credential.
 */

import type {
    MarketIntelDestinationsResponse,
    MarketIntelDirection,
    MarketIntelEquipmentResponse,
    MarketIntelHeatResponse,
    MarketIntelLanePriceRequest,
    MarketIntelLanePriceResponse,
    MarketIntelLaneQuery,
    MarketIntelTier,
    MarketIntelTopLoadsResponse,
} from '@numeo/types-core'
import { loadsIngestServiceRootAxios } from 'setup/SetupAxios'

interface Envelope<T> {
    success: boolean
    data: T
    message?: string
}

const BASE = '/market-intel'

export const marketIntelApi = {
    /** The equipment codes the snapshot holds, busiest first. */
    getEquipment: async (): Promise<MarketIntelEquipmentResponse> => {
        const res = await loadsIngestServiceRootAxios.get<Envelope<MarketIntelEquipmentResponse>>(`${BASE}/equipment/v1`)
        return res.data.data
    },

    /** J2 paint — one entry per area at `tier` with freight in the window. */
    getHeat: async (equipment: string, tier: MarketIntelTier, direction: MarketIntelDirection): Promise<MarketIntelHeatResponse> => {
        const res = await loadsIngestServiceRootAxios.get<Envelope<MarketIntelHeatResponse>>(`${BASE}/areas/heat/v1`, {
            params: { equipment, tier, direction },
        })
        return res.data.data
    },

    /** J2 click — where freight leaving `cell` pays best, already ranked. */
    getDestinations: async (cell: string, equipment: string, tier: MarketIntelTier, limit = 20): Promise<MarketIntelDestinationsResponse> => {
        const res = await loadsIngestServiceRootAxios.get<Envelope<MarketIntelDestinationsResponse>>(
            `${BASE}/areas/${encodeURIComponent(cell)}/destinations/v1`,
            { params: { equipment, tier, limit } }
        )
        return res.data.data
    },

    /** J3 — open loads out of `cell` right now, best $/mi first. Live, not from the snapshot. */
    getTopLoads: async (cell: string, equipment: string, limit = 20): Promise<MarketIntelTopLoadsResponse> => {
        const res = await loadsIngestServiceRootAxios.get<Envelope<MarketIntelTopLoadsResponse>>(`${BASE}/areas/${encodeURIComponent(cell)}/top-loads/v1`, {
            params: { equipment, limit },
        })
        return res.data.data
    },

    /** J1 + J4 — positional: `lanes[i]` answers `lanes[i]`, null when there is no honest answer. */
    priceLanes: async (lanes: MarketIntelLaneQuery[]): Promise<MarketIntelLanePriceResponse> => {
        const body: MarketIntelLanePriceRequest = { lanes }
        const res = await loadsIngestServiceRootAxios.post<Envelope<MarketIntelLanePriceResponse>>(`${BASE}/lanes/price/v1`, body)
        return res.data.data
    },
}
